/**
 * Postback API (FS-174) — dash data-layer client.
 *
 * Thin typed wrappers around `apiClient` for `/api/v1/postback-api`:
 *
 *   GET    /secrets             list tenant secrets (preview only, never plain)
 *   POST   /secrets              generate a new secret, returns plain ONCE
 *   DELETE /secrets/:id          revoke (soft delete)
 *   GET    /events?limit=        recent ingress audit events
 *
 * Pure helpers for the page (validation + status labels + copy-once modal
 * state) live here too so they can be unit-tested without rendering React.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types — mirror server shapes.
// ---------------------------------------------------------------------------

export interface SecretView {
	id: string;
	name: string;
	secret_preview: string;
	active: boolean;
	created_at: number;
	revoked_at: number | null;
}

/** Only returned from POST /secrets — the plain key is visible exactly once. */
export interface NewSecretView extends SecretView {
	plain: string;
}

export interface EventView {
	id: string;
	click_id: string;
	goal: string;
	revenue_cents: number;
	sig_valid: boolean;
	accepted: boolean;
	created_at: number;
}

// ---------------------------------------------------------------------------
// Validation — name for a secret.
// ---------------------------------------------------------------------------

const NAME_MAX = 120;

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface SecretDraft {
	name: string;
}

export const EMPTY_SECRET_DRAFT: SecretDraft = { name: "" };

export const secretDraftSchema = {
	safeParse(input: SecretDraft): ParseResult<{ name: string }> {
		const errors: Record<string, string> = {};
		const name = (input.name ?? "").trim();
		if (name.length === 0) {
			errors.name = "Name is required.";
		} else if (name.length > NAME_MAX) {
			errors.name = `Name must be ${NAME_MAX} chars or fewer.`;
		}
		if (Object.keys(errors).length > 0) return { success: false, errors };
		return { success: true, data: { name } };
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers.
// ---------------------------------------------------------------------------

export type EventStatusChip = "accepted" | "duplicate" | "rejected" | "pending";

/** Derive a compact chip label from the flags returned by the server. */
export function deriveEventStatus(row: Pick<EventView, "accepted" | "sig_valid">): EventStatusChip {
	if (row.accepted) return "accepted";
	if (!row.sig_valid) return "rejected";
	return "duplicate";
}

export const EVENT_STATUS_LABELS: Record<EventStatusChip, string> = {
	accepted: "Accepted",
	duplicate: "Duplicate",
	rejected: "Rejected",
	pending: "Pending",
};

export function eventStatusBadgeClass(status: EventStatusChip): string {
	switch (status) {
		case "accepted":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "duplicate":
			return "bg-sky-50 text-sky-700 border-sky-200";
		case "rejected":
			return "bg-red-50 text-red-700 border-red-200";
		default:
			return "bg-zinc-50 text-zinc-700 border-zinc-200";
	}
}

export function formatTimestamp(seconds: number | null): string {
	if (seconds === null || seconds === undefined) return "\u2014";
	if (Number.isNaN(seconds)) return "\u2014";
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "\u2014";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

/** Format integer cents as a human-readable currency-free amount. */
export function formatRevenueCents(cents: number): string {
	if (!Number.isFinite(cents)) return "0.00";
	const sign = cents < 0 ? "-" : "";
	const abs = Math.abs(cents);
	const whole = Math.floor(abs / 100);
	const rem = (abs % 100).toString().padStart(2, "0");
	return `${sign}${whole}.${rem}`;
}

// ---------------------------------------------------------------------------
// Sample-request helper — we show operators how to curl the endpoint.
// ---------------------------------------------------------------------------

export interface CurlSampleArgs {
	baseUrl: string;
	clickId?: string;
	goal?: string;
	revenueCents?: number;
}

export function buildCurlSample(args: CurlSampleArgs): string {
	const base = (args.baseUrl ?? "https://api.example.com").replace(/\/+$/, "");
	const body = JSON.stringify({
		click_id: args.clickId ?? "<CLICK_ID>",
		goal: args.goal ?? "install",
		revenue_cents: args.revenueCents ?? 0,
	});
	// We intentionally use `$TS` and `$SIG` placeholder vars so operators know
	// they must compute these. Copying the sample verbatim will fail — the
	// modal's copy-once UX reinforces this.
	return [
		`TS=$(date +%s)`,
		`BODY='${body}'`,
		`SIG=$(printf '%s' "$TS.$BODY" | openssl dgst -sha256 -hmac "$POSTBACK_SECRET" -hex | awk '{print $2}')`,
		`curl -X POST '${base}/api/v1/postback-api/v1/conversion' \\`,
		`  -H 'content-type: application/json' \\`,
		`  -H "X-Trackerity-Timestamp: $TS" \\`,
		`  -H "X-Trackerity-Signature: sha256=$SIG" \\`,
		`  --data-raw "$BODY"`,
	].join("\n");
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/postback-api";

export async function listSecrets(): Promise<SecretView[]> {
	return apiClient.get<SecretView[]>(`${BASE}/secrets`);
}

export async function createSecret(name: string): Promise<NewSecretView> {
	return apiClient.post<NewSecretView>(`${BASE}/secrets`, { name });
}

export async function revokeSecret(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/secrets/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export async function listEvents(limit = 50): Promise<EventView[]> {
	return apiClient.get<EventView[]>(`${BASE}/events`, { query: { limit } });
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const postbackApiQueryKeys = {
	all: ["postback-api"] as const,
	secrets: () => [...postbackApiQueryKeys.all, "secrets"] as const,
	events: (limit: number) => [...postbackApiQueryKeys.all, "events", limit] as const,
};
